import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { getActorDescription, getActorDisplayName } from '../../utils/roleUtils';
import { Badge } from '../ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import LoadingRing from '../ui-components/LoadingRing';
import type { AlignmentUser } from '../../utils/permissionAlignment';
import {
    getDeptPermissionTemplates,
    upsertDeptPermissionTemplate,
    acknowledgeTemplateChanges,
    type PermissionTemplate,
} from '../../services/api/departmentPermissionService';
import DeptPermissionTemplateDrawer from './DeptPermissionTemplateDrawer';
import { useToast } from '../../hooks/use-toast';
import type { FacilityPageViewer } from './utils/facilityPageTypes';
import type { FacilityUser } from '../../services/api/facilityService';

export const DEPARTMENT_ROLES_SECTION_ID = 'department-roles';

interface DeptPermissionTemplatesSectionProps {
    departmentId: string;
    viewer: FacilityPageViewer;
    users: FacilityUser[];
    canEditTemplates: boolean;
    /** Shown during onboarding: unconfigured roles fall back to NI defaults. */
    showDefaultHint?: boolean;
    onUsersChanged?: () => void;
}

export default function DeptPermissionTemplatesSection({
    departmentId,
    viewer,
    users,
    canEditTemplates,
    showDefaultHint = false,
    onUsersChanged,
}: DeptPermissionTemplatesSectionProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [templates, setTemplates] = useState<PermissionTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState<PermissionTemplate | null>(null);

    const isNiAdmin = viewer === 'niAdmin';
    const deptId = isNiAdmin ? departmentId : undefined;

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getDeptPermissionTemplates(deptId);
            setTemplates(data);
        } catch {
            toast({
                title: t('facilities.departmentRoles.loadError'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [deptId, toast, t]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const updateTemplate = useCallback(
        (actorId: string, patch: Partial<PermissionTemplate>) => {
            setTemplates((prev) =>
                prev.map((t) =>
                    t.actor_id === actorId ? { ...t, ...patch } : t
                )
            );
            // The drawer renders from editTarget, so mirror the patch there
            // to keep an open drawer in sync.
            setEditTarget((prev) =>
                prev && prev.actor_id === actorId ? { ...prev, ...patch } : prev
            );
        },
        []
    );

    const handleApply = async (
        deptPermissions: string[],
        mode: 'default' | 'custom'
    ) => {
        if (!editTarget) return;
        const { affectedUserCount } = await upsertDeptPermissionTemplate(
            editTarget.actor_id,
            deptPermissions,
            mode,
            deptId
        );
        updateTemplate(editTarget.actor_id, {
            mode,
            deptPermissions:
                mode === 'default'
                    ? editTarget.globalPermissions ?? []
                    : deptPermissions,
            hasOverride: mode === 'custom',
            hasUnacknowledgedChanges: false,
            alertType: null,
        });
        toast({
            title: t('facilities.departmentRoles.applyDrawer.appliedToast', {
                count: affectedUserCount,
            }),
        });
        onUsersChanged?.();
    };

    const handleAcknowledge = async (template: PermissionTemplate) => {
        try {
            await acknowledgeTemplateChanges(template.actor_id, deptId);
            updateTemplate(template.actor_id, {
                hasUnacknowledgedChanges: false,
                alertType: null,
            });
        } catch {
            toast({
                title: t('facilities.departmentRoles.acknowledgeFailed'),
                variant: 'destructive',
            });
        }
    };

    const getActorUsers = useCallback(
        (template: PermissionTemplate): AlignmentUser[] =>
            users
                .filter(
                    (u) => u.roleId === template.actor_id && !u.isInvite
                )
                .map((u) => ({
                    _id: u._id,
                    name: u.name || null,
                    email: u.email,
                    deptPermissions: u.deptPermissions ?? [],
                })),
        [users]
    );

    if (loading) {
        return (
            <div className="mt-8" id={DEPARTMENT_ROLES_SECTION_ID}>
                <h2 className="mb-4 text-lg font-semibold">
                    {t('facilities.departmentRoles.sectionTitle')}
                </h2>
                <div className="flex justify-center py-8">
                    <LoadingRing size="small" />
                </div>
            </div>
        );
    }

    if (templates.length === 0) return null;

    return (
        <div
            className={`mt-8 scroll-mt-6 ${showDefaultHint ? 'pt-6' : ''}`}
            id={DEPARTMENT_ROLES_SECTION_ID}
        >
            <h2 className="text-lg font-semibold">
                {t(
                    showDefaultHint
                        ? 'facilities.departmentRoles.setupTitle'
                        : 'facilities.departmentRoles.sectionTitle'
                )}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                {t('facilities.departmentRoles.sectionDescription')}
            </p>
            {showDefaultHint && (
                <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                    <p className="text-sm leading-relaxed text-foreground/90">
                        {t('facilities.departmentRoles.defaultFallbackHint')}
                    </p>
                </div>
            )}

            <div className="mt-4">
                <Table containerClassName="rounded-lg border border-white/5 bg-white/5">
                    <TableHeader className="bg-white/5 [&_tr]:border-white/5">
                        <TableRow className="hover:bg-transparent">
                            <TableHead>
                                {t('facilities.departmentRoles.table.role')}
                            </TableHead>
                            <TableHead>
                                {t('facilities.departmentRoles.table.description')}
                            </TableHead>
                            <TableHead>
                                {t('facilities.departmentRoles.table.type')}
                            </TableHead>
                            <TableHead>
                                {t('facilities.departmentRoles.table.permissions')}
                            </TableHead>
                            <TableHead>
                                {t('facilities.departmentRoles.table.users')}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {templates.map((tmpl) => (
                            <RoleTableRow
                                key={tmpl.actor_id}
                                template={tmpl}
                                actorUsers={getActorUsers(tmpl)}
                                onClick={() => setEditTarget(tmpl)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <DeptPermissionTemplateDrawer
                open={!!editTarget}
                onOpenChange={(open) => !open && setEditTarget(null)}
                roleName={editTarget ? getDisplayName(editTarget) : ''}
                template={editTarget}
                users={editTarget ? getActorUsers(editTarget) : []}
                canEdit={canEditTemplates}
                onApply={handleApply}
                onAcknowledge={() => {
                    if (editTarget) handleAcknowledge(editTarget);
                }}
            />
        </div>
    );
}

function RoleTableRow({
    template,
    actorUsers,
    onClick,
}: {
    template: PermissionTemplate;
    actorUsers: AlignmentUser[];
    onClick: () => void;
}) {
    const { t } = useTranslation();
    const displayName = getDisplayName(template);
    const isCustom = template.mode === 'custom';
    const isUnconfigured = template.mode === 'unconfigured';
    const purpose = getActorDescription({
        description: template.actorDescription,
        alternativeDescriptions: template.alternativeDescriptions,
    });

    return (
        <TableRow
            className="cursor-pointer border-white/5"
            onClick={onClick}
        >
            <TableCell>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                        {displayName}
                    </span>
                    {template.hasUnacknowledgedChanges && (
                        <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                </div>
            </TableCell>
            <TableCell>
                <div className="max-w-md">
                    {purpose && (
                        <p className="text-xs text-muted-foreground">
                            {purpose}
                        </p>
                    )}
                    {isUnconfigured && (
                        <p className="mt-0.5 text-xs text-amber-400/90">
                            {t('facilities.departmentRoles.unconfiguredInfo')}
                        </p>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <Badge
                    variant="outline"
                    className={`border-transparent text-xs ${
                        isUnconfigured
                            ? 'bg-amber-500/15 text-amber-400'
                            : isCustom
                            ? 'bg-blue-500/15 text-blue-400'
                            : 'bg-muted text-muted-foreground'
                    }`}
                >
                    {isUnconfigured
                        ? t('facilities.departmentRoles.badgeUnconfigured')
                        : isCustom
                        ? t('facilities.departmentRoles.modeCustom')
                        : t('facilities.departmentRoles.modeDefault')}
                </Badge>
            </TableCell>
            <TableCell>
                <span className="text-xs tabular-nums text-muted-foreground">
                    {template.deptPermissions.length}
                </span>
            </TableCell>
            <TableCell>
                <span className="text-xs tabular-nums text-muted-foreground">
                    {actorUsers.length}
                </span>
            </TableCell>
        </TableRow>
    );
}

function getDisplayName(template: PermissionTemplate): string {
    return getActorDisplayName({
        role: template.actorRole ?? '',
        alternativeNames: template.alternativeNames,
    });
}
